import { call, put, select } from "redux-saga/effects";
import {
  eventUpdateOne,
  addChosenEvent,
} from "../slices/events.slice";
import {
  addChosenSticker,
} from "../slices/stickers.slice";
import {
  studentAddOne,
  studentUpdateOne,
} from "../slices/students.slice";
import { scheduleService, studentsService } from "../../services";
import { enrichStickerWithColors } from "./index";

// Handle student-state updates when event students change on update
export function* handleStudentsOnEventUpdate({ prevEvent, eventPayload, eventId }) {
  const previousStudents =
    prevEvent?.students?.map((student) => student.id) || [];
  const currentStudents =
    eventPayload.students?.map((student) => student.id) || [];
  const removedStudentIds = previousStudents.filter(
    (studentId) => !currentStudents.includes(studentId)
  );

  if (removedStudentIds.length === 0) {
    return;
  }

  const studentEntities = yield select(
    (state) => state.students.entities || {}
  );

  for (const studentId of removedStudentIds) {
    const studentEntity = studentEntities?.[studentId];
    if (studentEntity) {
      const updatedStickers = (studentEntity.stickers || []).filter(
        (sticker) => +sticker.id !== +eventId
      );
      const fallbackStudent =
        prevEvent?.students?.find(
          (student) => +student.id === +studentId
        ) || {};
      yield put(
        studentUpdateOne({
          studentId,
          name: studentEntity.name,
          birthDate:
            studentEntity.birthDate ?? fallbackStudent.birthDate ?? null,
          stickers: updatedStickers,
        })
      );
    }
  }
}

// Detach a student from event and update both event and student state
export function* detachStudentFromEvent(action) {
  try {
    yield call(scheduleService.update, action.payload.body);
    const eventPayload = yield* enrichStickerWithColors({
      ...action.payload.body,
      students: action.payload.attachedStudents || [],
    });
    yield put(eventUpdateOne(eventPayload));
    yield put(addChosenEvent(eventPayload));
    yield put(
      addChosenSticker(
        yield* enrichStickerWithColors({
          ...action.payload.prevEvent,
          ...eventPayload,
          students: eventPayload.students,
        })
      )
    );

    const studentEntities = yield select(
      (state) => state.students.entities || {}
    );
    const studentEntity = studentEntities?.[action.payload.studentId];
    if (studentEntity) {
      const updatedStickers = (studentEntity.stickers || []).filter(
        (sticker) => +sticker.id !== +action.payload.body.id
      );
      const fallbackStudent =
        action.payload.prevEvent?.students?.find(
          (student) => +student.id === +action.payload.studentId
        ) || {};
      yield put(
        studentUpdateOne({
          studentId: action.payload.studentId,
          name: studentEntity.name,
          birthDate:
            studentEntity.birthDate ?? fallbackStudent.birthDate ?? null,
          stickers: updatedStickers,
        })
      );
    }
  } catch (error) {
    // Rethrow to let caller handle common error handling if needed
    throw error;
  }
}

// Attach student to event using studentsService and sync redux
export function* attachStudentToEvent(action) {
  try {
    const response = yield call(
      studentsService.addToSchedule,
      action.payload.body
    );
    const { student: newStudent, created } = response.data || {};

    if (!newStudent) {
      throw new Error("Failed to attach student");
    }

    const studentPayload = created ? [...action.payload.attachedStudents, newStudent] : [...action.payload.attachedStudents.filter(student => student.id !== newStudent.id), newStudent]
    const eventPayload = yield* enrichStickerWithColors({
      ...action.payload.prevEvent,
      ...action.payload.eventBody,
      students: studentPayload,
    });

    // Update event in state.events.data
    yield put(eventUpdateOne(eventPayload));

    // Update student in state.students
    if (created) {
      // Add new student to state.students
      yield put(studentAddOne({
        id: newStudent.id,
        name: newStudent.name,
        birthDate: newStudent.birthDate,
        stickers: Array.isArray(newStudent.stickers) ? newStudent.stickers : [],
      }));
    } else {
      // Update existing student with updated stickers list from server
      yield put(
        studentUpdateOne({
          studentId: newStudent.id,
          name: newStudent.name,
          birthDate: newStudent.birthDate,
          stickers: Array.isArray(newStudent.stickers) ? newStudent.stickers : [],
        })
      );
    }

    yield put(
      addChosenSticker(
        yield* enrichStickerWithColors({
          ...action.payload.prevEvent,
          ...eventPayload,
          students: eventPayload.students,
        })
      )
    );
  } catch (error) {
    // Rethrow to let caller handle common error handling if needed
    throw error;
  }
}
